import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import ActivityLogsDelegate from './activityLogs.delegate';
import { canAccess } from '../../core/access';
import dayjs from 'dayjs';

class ActivityLogsController extends Inherit(ShelfController, ActivityLogsDelegate) {
    async initialize() {
        if (!this.isAuthenticated) {
            this.navigateLogin();
            return;
        }

        // Authorization comes from the central access model (see core/access.js) so this
        // screen can't drift out of sync with the sidebar and route guards.
        const userType = this.user?.userType;
        if (!canAccess(userType, 'activity-logs')) {
            console.warn('⚠️ Unauthorized access to activity logs - redirecting to dashboard');
            this.navigateTo('/dashboard');
            return;
        }

        // Fetch tags and logs in parallel (with initial date filters from state)
        await Promise.all([
            this.fetchTags(),
            this.refreshLogs() // Use refreshLogs to apply initial date filters
        ]);
    }

    async fetchTags() {
        try {
            const response = await this.data.fetchTags({ active: true });
            const tags = response?.data?.tags || response?.tags || response || [];
            this.setState({ availableTags: Array.isArray(tags) ? tags : [] });
        } catch (error) {
            console.error('Error fetching tags:', error);
            this.setState({ availableTags: [] });
        }
    }

    async fetchActivityLogsData(filters = {}) {
        try {
            this.setState({ loading: true });

            const [logsResponse, stats] = await Promise.all([
                this.data.fetchActivityLogs(filters),
                this.data.fetchActivityLogsStats(filters)
            ]);

            const logs = logsResponse?.data?.logs || [];
            const pagination = logsResponse?.data?.pagination || {};

            this.setState({
                logs,
                pagination,
                stats: stats?.data || null,
                loading: false
            });
        } catch (error) {
            console.error('Error fetching activity logs:', error);
            this.setState({ loading: false });
        }
    }

    async refreshLogs() {
        const { filterAction, filterEntity, filterUserType, filterUser, filterTag, startDate, endDate, page, search } = this.state;
        const filters = { page, limit: 50 };

        if (filterAction && filterAction !== 'all') filters.action = filterAction;
        if (filterEntity && filterEntity !== 'all') filters.entityType = filterEntity;
        if (filterUserType && filterUserType !== 'all') filters.userType = filterUserType;
        if (filterUser && filterUser !== 'all') filters.userId = filterUser;
        if (filterTag && filterTag !== 'all') filters.tagId = filterTag;
        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;
        if (search) filters.search = search;

        await this.fetchActivityLogsData(filters);
    }

    setFilterAction(action) {
        this.setState({ filterAction: action, page: 1 }, () => this.refreshLogs());
    }

    setFilterEntity(entityType) {
        this.setState({ filterEntity: entityType, page: 1 }, () => this.refreshLogs());
    }

    setFilterUserType(userType) {
        this.setState({ filterUserType: userType, page: 1 }, () => this.refreshLogs());
    }

    setFilterUser(userId) {
        this.setState({ filterUser: userId, page: 1 }, () => this.refreshLogs());
    }

    setFilterTag(tagId) {
        this.setState({ filterTag: tagId, page: 1 }, () => this.refreshLogs());
    }

    setDateRange(startDate, endDate) {
        this.setState({ startDate, endDate, page: 1 }, () => this.refreshLogs());
    }

    setSearch(search) {
        this.setState({ search, page: 1 }, () => this.refreshLogs());
    }

    // Quick period presets (Hoy, Ayer, 7/30 días). "custom" only reveals the range picker.
    handleDatePresetChange(preset) {
        let startDate, endDate;
        const today = dayjs();

        switch (preset) {
            case 'today':
                startDate = today.format('YYYY-MM-DD');
                endDate = today.format('YYYY-MM-DD');
                break;
            case 'yesterday':
                startDate = today.subtract(1, 'day').format('YYYY-MM-DD');
                endDate = today.subtract(1, 'day').format('YYYY-MM-DD');
                break;
            case 'week':
                startDate = today.subtract(7, 'day').format('YYYY-MM-DD');
                endDate = today.format('YYYY-MM-DD');
                break;
            case 'month':
                startDate = today.subtract(30, 'day').format('YYYY-MM-DD');
                endDate = today.format('YYYY-MM-DD');
                break;
            case 'custom':
                this.setState({ datePreset: preset, showCustomDate: true });
                return;
            default:
                startDate = today.subtract(7, 'day').format('YYYY-MM-DD');
                endDate = today.format('YYYY-MM-DD');
        }

        this.setState({
            datePreset: preset,
            startDate,
            endDate,
            showCustomDate: false,
            page: 1
        }, () => this.refreshLogs());
    }

    setCustomDateRange(dates) {
        if (!dates) return;
        this.setState({
            startDate: dates[0]?.format('YYYY-MM-DD'),
            endDate: dates[1]?.format('YYYY-MM-DD'),
            page: 1,
        }, () => this.refreshLogs());
    }

    clearFilters() {
        this.setState({
            filterAction: 'all',
            filterEntity: 'all',
            filterUserType: 'all',
            filterUser: 'all',
            filterTag: 'all',
            datePreset: 'week',
            startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            showCustomDate: false,
            search: '',
            page: 1,
        }, () => this.refreshLogs());
    }

    setPage(page) {
        this.setState({ page }, () => this.refreshLogs());
    }

    async viewLogDetails(logId) {
        try {
            const response = await this.data.fetchActivityLogById(logId);
            if (response?.data) {
                this.setState({ selectedLog: response.data, showDetailModal: true });
            }
        } catch (error) {
            console.error('Error fetching log details:', error);
        }
    }

    closeDetailModal() {
        this.setState({ selectedLog: null, showDetailModal: false });
    }
}

export default ActivityLogsController;
